import React, { useState } from 'react';
import { Search, Trash2, Eye, Clock, Pencil, FileCheck } from 'lucide-react';
import { Quote, User } from '../types';
import { formatCurrency, generateInvoiceNumber } from '../utils/helpers';
import QuoteQuickLook from '../components/QuoteQuickLook';

interface QuoteHistoryProps { quotes: Quote[]; user: User; onDelete: (id: string) => void; onEdit?: (quote: Quote) => void; onUpdateStatus?: (id: string, status: string) => void; onConvertToInvoice?: (id: string, invoiceNumber: string) => void; onMarkAdvanceReceived?: (id: string) => void; }

const STATUS_OPTIONS = ['all', 'draft', 'sent', 'confirmed', 'cancelled'];
const STATUS_COLORS: Record<string, string> = { draft: 'badge-amber', sent: 'badge-teal', confirmed: 'badge-best', cancelled: 'badge-red' };

function tierBadgeClass(tier: string): string {
  if (tier === 'Best') return 'badge-best';
  if (tier === 'Luxe') return 'badge-luxe';
  if (tier === 'Elite') return 'badge-elite';
  return 'badge-amber'; // General Planner / unknown
}

export default function QuoteHistory({ quotes, user, onDelete, onEdit, onUpdateStatus, onConvertToInvoice, onMarkAdvanceReceived }: QuoteHistoryProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [previewId, setPreviewId] = useState<string | null>(null);
  const preview = previewId ? quotes.find(q => q.id === previewId) || null : null;

  const filtered = quotes.filter(q => {
    const matchSearch = !search || q.customerName.toLowerCase().includes(search.toLowerCase()) || q.quoteNumber.toLowerCase().includes(search.toLowerCase()) || q.eventName.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || q.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div style={{ padding: 28, maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1>Quote History</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>{quotes.length} total quotes</p>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input className="input" style={{ paddingLeft: 32 }} placeholder="Search by name, event, quote number…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="select" style={{ width: 160 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s === 'all' ? 'All Statuses' : s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>
          <Clock size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p>No quotes found.</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Quote #</th><th>Customer</th><th>Event</th><th>Packages</th>
                <th>Created</th><th>Amount</th><th>Status</th><th>Actions</th>
              </tr></thead>
              <tbody>
                {filtered.map(q => (
                  <tr key={q.id}>
                    <td><span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{q.quoteNumber}</span></td>
                    <td style={{ fontWeight: 500 }}>{q.customerName}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{q.eventName}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {q.packageItems?.slice(0, 2).map((p, i) => (
                          <span key={i} className={`badge ${tierBadgeClass(p.qualityTier)}`} style={{ fontSize: 10 }}>{p.qualityTier}</span>
                        ))}
                        {(q.packageItems?.length || 0) > 2 && <span className="badge badge-amber" style={{ fontSize: 10 }}>+{q.packageItems.length - 2}</span>}
                      </div>
                    </td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{new Date(q.createdAt).toLocaleDateString('en-IN')}</td>
                    <td style={{ fontWeight: 700, color: 'var(--gold)' }}>{formatCurrency(q.quotedAmount)}</td>
                    <td><span className={`badge ${STATUS_COLORS[q.status]}`}>{q.status}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        {/* QUOTATION / INVOICE (awaiting advance / advance received) badge */}
                        {(() => {
                          const isInvoice = (q as any).documentType === 'invoice';
                          const advanceIn = (q as any).advanceReceived;
                          const label = !isInvoice ? 'QUOTATION' : advanceIn ? 'INVOICE · PAID' : 'INVOICE · AWAITING ADVANCE';
                          const bg = !isInvoice ? '#EBF8FF' : advanceIn ? '#D1FAE5' : '#FEF3C7';
                          const fg = !isInvoice ? '#1E3A5F' : advanceIn ? '#065F46' : '#92400E';
                          const bd = !isInvoice ? '#90CDF4' : advanceIn ? '#6EE7B7' : '#FCD34D';
                          return (
                            <span style={{ fontSize:10, fontWeight:700, padding:'2px 6px', borderRadius:4, background:bg, color:fg, border:`1px solid ${bd}` }}>
                              {label}
                            </span>
                          );
                        })()}
                        <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setPreviewId(q.id)} title="Preview">
                          <Eye size={13} />
                        </button>
                        {onEdit && !(q as any).isGeneralPlanner && (
                          <button className="btn btn-ghost btn-sm btn-icon" onClick={() => onEdit(q)} title="Edit">
                            <Pencil size={13} />
                          </button>
                        )}
                        {/* Convert to Invoice — green FileCheck icon. Creates the invoice only; does not imply payment. */}
                        {onConvertToInvoice && (q as any).documentType !== 'invoice' && (
                          <button className="btn btn-ghost btn-sm btn-icon" title="Convert to Invoice"
                            onClick={() => {
                              if (window.confirm(`Convert ${q.quoteNumber} to Invoice?\n\nThis creates the invoice and marks it ready to send to ${q.customerName}. It does NOT confirm payment — use "Confirm Advance Received" separately once the advance actually comes in.`)) {
                                onConvertToInvoice(q.id, generateInvoiceNumber());
                              }
                            }}>
                            <FileCheck size={13} color="var(--teal)" />
                          </button>
                        )}
                        {/* Confirm Advance Received — only once invoiced, only until confirmed */}
                        {onMarkAdvanceReceived && (q as any).documentType === 'invoice' && !(q as any).advanceReceived && (
                          <button className="btn btn-ghost btn-sm btn-icon" title="Confirm Advance Received"
                            onClick={() => {
                              if (window.confirm(`Confirm advance received from ${q.customerName} for ${q.quoteNumber}?`)) {
                                onMarkAdvanceReceived(q.id);
                              }
                            }}>
                            <FileCheck size={13} color="var(--gold)" />
                          </button>
                        )}
                        {user.role === 'admin' && (
                          <button className="btn btn-ghost btn-sm btn-icon" onClick={() => { if (window.confirm('Delete this quote?')) onDelete(q.id); }} title="Delete">
                            <Trash2 size={13} color="var(--red-light)" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Quick-look preview */}
      {preview && (
        <QuoteQuickLook
          quote={preview}
          onClose={() => setPreviewId(null)}
          onEdit={onEdit}
          onStatusChange={onUpdateStatus}
          onConvertToInvoice={onConvertToInvoice}
          onMarkAdvanceReceived={onMarkAdvanceReceived}
        />
      )}
    </div>
  );
}
